import { service } from '@ember/service'
import Component from '@glimmer/component'
import { SeriesLine } from 'teacher-ui-client/components/giraffe/primitives/series-line'
import { type IntlService } from 'ember-intl'
import { t } from 'ember-intl'
import type { UIScopeName } from 'teacher-ui-client/reporting/ui-scope/route'
import convertDateFormat from 'teacher-ui-client/utils/giraffe/convert-date-format'
import type { SeriesLineOptions } from 'highcharts'
import { fullGradeName } from 'teacher-ui-client/helpers/full-grade-name'
import { colors } from '@colors'
import type { AveragesReportDataForGrade } from 'teacher-ui-client/components/reporting/writing-legends/course-progress/averages/report-loader'
import { maxBy } from 'lodash'

interface Signature {
  Args: {
    averagesReportForGrade: AveragesReportDataForGrade
    scopeName: UIScopeName
  }
}

export class CourseProgressAveragesReport extends Component<Signature> {
  @service intl!: IntlService

  get months(): AveragesReportDataForGrade['months'] {
    return this.args.averagesReportForGrade.months
  }

  get title() {
    switch (this.args.scopeName) {
      case 'teacher':
        return this.intl.t('reporting.chartTitles.courseProgressAverages.teacher')
      case 'school-class':
        return this.intl.t('reporting.chartTitles.courseProgressAverages.schoolClass')
      case 'school':
        return this.intl.t('reporting.chartTitles.courseProgressAverages.school')
      default:
        return ''
    }
  }

  get seriesLineChartData(): SeriesLineOptions[] {
    const name = 'avg_max_lesson'
    return [
      {
        name,
        color: colors.oceanyBlue325,
        data: this.months.map((monthData, index) => ({
          x: index,
          y: monthData[name],
          monthData,
        })),
        type: 'line',
      },
    ]
  }

  /**
   * Converts month dates into short names for use as column labels in Highcharts.
   */
  get categories() {
    return this.months.map(({ month }) => convertDateFormat(month))
  }

  /**
   * Set the yRange of the chart from 1 to the max lesson in the dataset + 1.
   */
  get yRange() {
    const max = maxBy(this.months, 'avg_max_lesson')?.avg_max_lesson
    return typeof max === 'number' && max > 0 ? [1, max + 1] : [-Infinity, Infinity]
  }

  <template>
    <SeriesLine
      class="pt-2 pb-4"
      @title="{{this.title}} - {{fullGradeName @averagesReportForGrade.grade_position}}"
      @data={{this.seriesLineChartData}}
      @categories={{this.categories}}
      @yAxisLabel={{t "reporting.charts.courseProgress.averages.yAxisLabel"}}
      @yRange={{this.yRange}}
    />
  </template>
}
